/**
 * SCORM bridge for the SCORM 2004 package generated from the Master Dashboard.
 *
 * The package's launch page (running inside the LMS) opens the game in a new tab with
 * `?scorm=1&scenario=<id>&lmsOrigin=<origin>`. At startup the game asks that tab
 * (`window.opener`) for the learner id and package key and exchanges them for a session
 * (`POST /api/scorm/session`), so the learner never logs in. The game then reports start
 * and completion back to the launch page, which forwards them to the LMS.
 *
 * The launch is kept in sessionStorage (per tab) so it survives the URL being rewritten.
 */

export const SCORM_MESSAGE_SOURCE = 'cybernovel-scorm';
const STORAGE_KEY = 'cybernovel_scorm_launch';
const ID_PATTERN = /^[A-Za-z0-9._-]{1,200}$/;
const IDENTITY_TIMEOUT_MS = 3000;

export const SCORM_LOGIN_FAILED_NOTICE =
  'Connexion automatique impossible : relancez le module depuis votre plateforme de formation.';

export interface ScormLaunch {
  scenarioId: string;
  lmsOrigin: string;
  autoStarted: boolean;
}

export interface ScormIdentity {
  key: string;
  learnerId: string;
}

export type ScormMessage =
  | { type: 'identity-request'; scenarioId: string }
  | { type: 'ready'; scenarioId: string }
  | { type: 'scenario-complete'; scenarioId: string }
  | { type: 'error'; scenarioId: string; message: string };

interface MessageTarget {
  closed?: boolean;
  postMessage(message: unknown, targetOrigin: string): void;
}

interface MessageListenerTarget {
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

function defaultStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Accepts only a bare http(s) origin, e.g. `https://lms.example.fr`. */
export function parseLmsOrigin(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin === value ? value : null;
  } catch {
    return null;
  }
}

export function parseScormLaunch(search: string): ScormLaunch | null {
  const params = new URLSearchParams(search);
  if (params.get('scorm') !== '1') return null;

  const scenarioId = params.get('scenario');
  const lmsOrigin = parseLmsOrigin(params.get('lmsOrigin'));
  if (!scenarioId || !ID_PATTERN.test(scenarioId) || !lmsOrigin) return null;

  return { scenarioId, lmsOrigin, autoStarted: false };
}

/**
 * Stores the SCORM launch found in the URL. A SCORM URL (first open, or a reload while
 * playing) resets `autoStarted` so the scenario is opened again once logged in.
 */
export function captureScormLaunch(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
  storage: Storage | null = defaultStorage(),
): void {
  const launch = parseScormLaunch(search);
  if (!launch || !storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(launch));
  } catch {
    // Storage unavailable: the game still works, without LMS tracking.
  }
}

export function getScormLaunch(storage: Storage | null = defaultStorage()): ScormLaunch | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScormLaunch>;
    const lmsOrigin = parseLmsOrigin(parsed.lmsOrigin ?? null);
    if (typeof parsed.scenarioId !== 'string' || !ID_PATTERN.test(parsed.scenarioId) || !lmsOrigin) {
      return null;
    }
    return { scenarioId: parsed.scenarioId, lmsOrigin, autoStarted: parsed.autoStarted === true };
  } catch {
    return null;
  }
}

export function markScormAutoStarted(storage: Storage | null = defaultStorage()): void {
  const launch = getScormLaunch(storage);
  if (!launch || !storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ ...launch, autoStarted: true }));
  } catch {
    // Ignore: worst case the scenario is reopened once more after "back to menu".
  }
}

function defaultOpener(): MessageTarget | null {
  try {
    return typeof window === 'undefined' ? null : (window.opener as MessageTarget | null);
  } catch {
    return null;
  }
}

/** Sends a message to the SCORM launch tab. Returns false when there is nothing to report to. */
export function postToLms(
  message: ScormMessage,
  opener: MessageTarget | null = defaultOpener(),
  storage: Storage | null = defaultStorage(),
): boolean {
  const launch = getScormLaunch(storage);
  if (!launch || launch.scenarioId !== message.scenarioId) return false;
  if (!opener || opener.closed) return false;
  try {
    opener.postMessage({ source: SCORM_MESSAGE_SOURCE, ...message }, launch.lmsOrigin);
    return true;
  } catch {
    return false;
  }
}

/**
 * Asks the launch page who is playing. Only an answer from the opener, on the LMS origin, is
 * accepted. Resolves null when there is no opener, no answer in time, or no learner id.
 */
export function requestScormIdentity(
  launch: ScormLaunch,
  options: {
    opener?: MessageTarget | null;
    listenTarget?: MessageListenerTarget;
    storage?: Storage | null;
    timeoutMs?: number;
  } = {},
): Promise<ScormIdentity | null> {
  const opener = options.opener === undefined ? defaultOpener() : options.opener;
  const listenTarget: MessageListenerTarget | null =
    options.listenTarget ?? (typeof window === 'undefined' ? null : (window as unknown as MessageListenerTarget));
  if (!opener || opener.closed || !listenTarget) return Promise.resolve(null);

  return new Promise((resolve) => {
    const finish = (identity: ScormIdentity | null) => {
      clearTimeout(timer);
      listenTarget.removeEventListener('message', onMessage);
      resolve(identity);
    };
    function onMessage(event: MessageEvent) {
      if (event.source !== opener || event.origin !== launch.lmsOrigin) return;
      const data = event.data as Record<string, unknown> | null;
      if (!data || data.source !== SCORM_MESSAGE_SOURCE || data.type !== 'identity') return;
      if (data.scenarioId !== launch.scenarioId) return;
      const valid = typeof data.key === 'string' && typeof data.learnerId === 'string' && data.learnerId.trim();
      finish(valid ? { key: data.key as string, learnerId: data.learnerId as string } : null);
    }

    listenTarget.addEventListener('message', onMessage);
    const timer = setTimeout(() => finish(null), options.timeoutMs ?? IDENTITY_TIMEOUT_MS);
    const sent = postToLms(
      { type: 'identity-request', scenarioId: launch.scenarioId },
      opener,
      options.storage === undefined ? defaultStorage() : options.storage,
    );
    if (!sent) finish(null);
  });
}

/** `none`: not a SCORM launch; `started`: the LMS learner is now logged in; `failed`: see the launch page. */
export type ScormSessionResult = 'none' | 'started' | 'failed';

export async function startScormSession(
  options: {
    opener?: MessageTarget | null;
    listenTarget?: MessageListenerTarget;
    storage?: Storage | null;
    timeoutMs?: number;
    fetchImpl?: typeof fetch;
  } = {},
): Promise<ScormSessionResult> {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  const launch = getScormLaunch(storage);
  if (!launch) return 'none';

  const opener = options.opener === undefined ? defaultOpener() : options.opener;
  const fail = (message: string): ScormSessionResult => {
    postToLms({ type: 'error', scenarioId: launch.scenarioId, message: message.slice(0, 500) }, opener, storage);
    return 'failed';
  };

  const identity = await requestScormIdentity(launch, { ...options, opener, storage });
  if (!identity) return fail('identifiant de la plateforme de formation indisponible.');

  try {
    const response = await (options.fetchImpl ?? fetch)('/api/scorm/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: identity.key, learnerId: identity.learnerId, scenarioId: launch.scenarioId }),
    });
    if (response.ok) return 'started';
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    return fail(body?.error || 'connexion automatique impossible.');
  } catch {
    return fail('connexion automatique impossible.');
  }
}

let sessionStart: Promise<ScormSessionResult> | null = null;

/** Runs the SCORM login at most once per page load (StrictMode mounts effects twice). */
export function startScormSessionOnce(): Promise<ScormSessionResult> {
  sessionStart ??= startScormSession();
  return sessionStart;
}

export function reportScormReady(scenarioId: string): void {
  postToLms({ type: 'ready', scenarioId });
}

export function reportScormScenarioComplete(scenarioId: string): void {
  postToLms({ type: 'scenario-complete', scenarioId });
}

export function reportScormError(scenarioId: string, message: string): void {
  postToLms({ type: 'error', scenarioId, message: message.slice(0, 500) });
}
